import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg, inr, fmtDate } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Pill, Money } from "@/components/common";
import { ClassifyForm } from "@/components/ClassifyForm";
import { BillMatch } from "@/components/BillMatch";

const KV = ({ k, v, mono }) => (
  <div className="flex justify-between gap-4 border-b py-2 text-sm last:border-0"><span className="text-muted-foreground">{k}</span><span className={`text-right ${mono ? "font-mono text-xs" : ""}`}>{v ?? "—"}</span></div>
);

function Correction({ t, onDone }) {
  const [f, setF] = useState({ txn_date: t.txn_date, debit: t.debit, credit: t.credit, balance: t.balance || "", ref: t.ref || "", reason: "" });
  const save = async () => {
    try {
      await api.patch(`/transactions/${t.id}`, f);
      toast.success("Corrected — original kept in audit trail, statement re-validated");
      onDone();
    } catch (e) { toast.error(await errMsg(e)); }
  };
  return (
    <div className="space-y-2 rounded-lg border border-dashed p-3">
      <div className="label-xs">Correct extracted values (compare with the PDF)</div>
      <div className="grid grid-cols-2 gap-2">
        {["txn_date", "ref", "debit", "credit", "balance"].map((k) => (
          <Input key={k} className="h-8 num text-xs" placeholder={k} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`correct-${k}-input`} />
        ))}
        <Input className="h-8 text-xs" placeholder="Reason (required)" value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} data-testid="correct-reason-input" />
      </div>
      <Button size="sm" variant="outline" onClick={save} data-testid="correct-save-button">Save correction</Button>
    </div>
  );
}

export function TxnDrawer({ id, onClose, onChanged, defaultTab }) {
  const { accounts, can } = useApp();
  const [t, setT] = useState(null);
  const load = () => api.get(`/transactions/${id}`).then((r) => setT(r.data)).catch(() => setT(null));
  useEffect(() => { if (id) { setT(null); load(); } /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [id]);
  const changed = () => { load(); onChanged?.(); };
  const act = async (action, extra = {}) => {
    try {
      const r = await api.post("/transactions/bulk", { ids: [id], action, ...extra });
      if (r.data.errors.length) toast.error(r.data.errors[0].error); else toast.success("Done");
      changed();
    } catch (e) { toast.error(await errMsg(e)); }
  };
  const acct = t && accounts.find((a) => a.id === t.account_id);
  const locked = t && ["exported", "imported"].includes(t.export_status);
  return (
    <Sheet open={!!id} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-2xl" data-testid="txn-drawer">
        {!t ? <div className="p-6 text-sm text-muted-foreground">Loading…</div> : (
          <>
            <SheetHeader>
              <SheetTitle className="font-display">
                <Money v={t.amount} dir={t.direction} className="text-2xl" /> <span className="ml-2 text-sm text-muted-foreground">{t.direction} · {fmtDate(t.txn_date)}</span>
              </SheetTitle>
              <div className="rounded-lg bg-muted p-3 font-mono text-xs leading-relaxed" data-testid="txn-original-narration">{t.narration}</div>
              <div className="flex flex-wrap gap-2 pt-1">
                <Pill s={t.status} testid="txn-drawer-status" /><Pill s={t.dup_status} /><Pill s={t.export_status} />
                {(t.tags || []).map((g) => <span key={g} className="rounded-full bg-accent px-2 text-[11px]">{g.replace("_", " ")}</span>)}
              </div>
            </SheetHeader>
            <div className="mt-4 flex flex-wrap gap-2">
              {t.status === "classified" && <Button size="sm" onClick={() => act("approve")} disabled={!can("admin", "accountant")} data-testid="drawer-approve-button">Approve (checker)</Button>}
              {t.dup_status === "possible_duplicate" && <>
                <Button size="sm" variant="outline" onClick={() => act("confirm_duplicate")} data-testid="drawer-confirm-dup">Confirm duplicate</Button>
                <Button size="sm" variant="outline" onClick={() => act("not_duplicate")} data-testid="drawer-not-dup">Not a duplicate</Button></>}
              {t.dup_status === "already_imported" && <Button size="sm" variant="outline" onClick={() => act("not_duplicate")} data-testid="drawer-not-dup">Not a duplicate — keep</Button>}
              {t.confidence < 0.8 && !t.extraction_verified && <Button size="sm" variant="outline" onClick={() => act("verify_extraction")} data-testid="drawer-verify-extraction">Mark verified against PDF</Button>}
              {!["suspense", "duplicate"].includes(t.status) && !locked && <Button size="sm" variant="ghost" onClick={() => act("suspense")} data-testid="drawer-suspense-button">Send to suspense</Button>}
            </div>
            <Tabs defaultValue={defaultTab || (t.status === "needs_review" || t.status === "suspense" ? "classify" : "details")} className="mt-5">
              <TabsList><TabsTrigger value="details">Details</TabsTrigger><TabsTrigger value="classify" data-testid="drawer-tab-classify">Classify</TabsTrigger>
                <TabsTrigger value="bills" data-testid="drawer-tab-bills">Bills</TabsTrigger><TabsTrigger value="history">History</TabsTrigger></TabsList>
              <TabsContent value="details" className="space-y-4">
                <div>
                  <KV k="Bank" v={acct ? `${acct.bank_name} ${acct.masked}` : "—"} /><KV k="Value date" v={fmtDate(t.value_date)} />
                  <KV k="UTR / Ref" v={t.ref} mono /><KV k="Cheque" v={t.cheque_no} mono /><KV k="Running balance" v={inr(t.balance)} mono />
                  <KV k="Mode" v={t.mode} /><KV k="Party (identified)" v={t.party_name || t.party_extracted} /><KV k="Party confidence" v={`${Math.round((t.party_confidence || 0) * 100)}% ${t.party_reason || ""}`} />
                  <KV k="Ledger" v={t.ledger} /><KV k="Voucher" v={t.voucher_type} /><KV k="Bill no" v={t.bill_no} />
                  <KV k="Source" v={`Page ${t.page}, row ${t.row_no} · ${t.source}`} /><KV k="Extraction confidence" v={`${Math.round(t.confidence * 100)}%${t.extraction_verified ? " · verified" : ""}`} />
                  {t.dup_reason && <KV k="Duplicate evidence" v={t.dup_reason} />}
                  {t.maker_name && <KV k="Maker / Checker" v={`${t.maker_name} / ${t.checker_name || "pending"}`} />}
                  {t.export_batch_id && <KV k="Export batch" v={t.export_batch_id} mono />}
                </div>
                <div className="rounded-lg border p-3">
                  <div className="label-xs mb-2">Immutable original (as extracted)</div>
                  <pre className="whitespace-pre-wrap font-mono text-[11px] text-muted-foreground" data-testid="txn-original-snapshot">{JSON.stringify(t.original, null, 1)}</pre>
                </div>
                {!locked && can("admin", "accountant") && <Correction t={t} onDone={changed} />}
              </TabsContent>
              <TabsContent value="classify">
                {locked ? <div className="text-sm text-muted-foreground">Locked after export. Mark the batch as import failed to unlock.</div> :
                  <ClassifyForm key={t.updated_at} txns={[t]} mode={t.status === "suspense" ? "suspense" : "classify"} onDone={changed} />}
              </TabsContent>
              <TabsContent value="bills"><BillMatch txn={t} onDone={changed} /></TabsContent>
              <TabsContent value="history">
                {(t.history || []).map((h) => (
                  <div key={h.id} className="border-b py-2 text-xs">
                    <span className="num text-muted-foreground">{h.at.slice(0, 19).replace("T", " ")}</span> · <b>{h.action}</b> by {h.user_name}{h.note ? ` — ${h.note}` : ""}
                  </div>
                ))}
              </TabsContent>
            </Tabs>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
